/**
 * Минимальная маршрутизация SPA по адресной строке.
 *
 * Flow: компонент вызывает useRoute() → получает текущий путь и функции
 *       перехода → переход пишет запись в историю браузера (pushState) и
 *       перерисовывает экран → «назад»/«вперёд» браузера приходят событием
 *       popstate и возвращают прежний путь. Отдельно resolveSectionAccess()
 *       сопоставляет путь с ВЫДАННЫМИ роли разделами и отвечает, показывать
 *       раздел, названный отказ или извещение.
 *
 * ПОЧЕМУ не react-router-dom. Разделов три, вложенных маршрутов нет,
 * параметров пути нет, загрузчиков данных нет — каждая панель ходит за
 * своими данными сама. Всё, что нужно от маршрутизатора, — это три вызова
 * браузерного API, и они ниже целиком. Библиотека дала бы те же три вызова
 * плюс постоянную зависимость: сегодня в package.json ровно react и
 * react-dom, и каждое добавление сюда — это обновления, уязвимости и вес
 * бандла, которые придётся сопровождать. Та же мера уже принята в api.js
 * («обёртка над HTTP не нуждается в библиотеке») и в eslint.config.js.
 * Обратное решение станет верным, когда появятся вложенные разделы с
 * параметрами в пути: переписать тридцать строк дешевле, чем тянуть
 * маршрутизатор ради двух пунктов меню.
 *
 * Маршрутизация по ПУТИ, а не по хешу (#/dealing-desk): путь виден серверу,
 * попадает в журналы nginx и выглядит адресом раздела, а не якорем внутри
 * страницы. Цена — обязательный fallback на index.html, он уже есть в
 * services/frontend/nginx.conf (`try_files $uri $uri/ /index.html`).
 *
 * Зависимости:
 *   - react: useState/useEffect/useCallback — состояние пути и подписка
 *     на событие истории.
 *
 * Связанные файлы:
 *   - src/App.jsx — единственный потребитель: рисует навигацию из выдачи
 *     разделов и выбирает раздел по пути;
 *   - docs/specs/BL-2026-10-08-a315-section-access.md — §5 (форма выдачи
 *     в GET /api/me) и §6 (поведение при закрытом разделе);
 *   - services/frontend/nginx.conf — SPA-fallback, без которого прямое
 *     открытие /dealing-desk отдало бы 404.
 */

import { useCallback, useEffect, useState } from "react";

/** Текущий путь адресной строки; пустой путь считается корнем. */
export function currentPath() {
  return window.location.pathname || "/";
}

/**
 * Решает по адресу, что показать: раздел, названный отказ или извещение.
 *
 * @param {string} path — путь из адресной строки
 * @param {Array<{code: string, title: string, path: string}>} sections —
 *        разделы, ВЫДАННЫЕ роли (GET /api/me, поле sections, в порядке
 *        сервера); не массив или пустой — выдачи нет
 * @param {function(string): boolean} canRender — умеет ли этот бандл
 *        нарисовать раздел с таким кодом (реестр SECTION_VIEWS в App.jsx)
 * @returns {{kind: string, section: object|null, path: string|null}} решение:
 *   - `empty` — роли не выдано ни одного раздела (section = null);
 *   - `section` — адрес указывает на выданный раздел, и его есть чем нарисовать;
 *   - `unsupported` — раздел выдан, но этот бандл его рисовать не умеет;
 *   - `redirect` — адрес равен корню приложения, а раздел с путём `/` роли
 *     не выдан: section — первый выданный раздел, path — его адрес;
 *   - `denied` — адрес не принадлежит ни одному выданному разделу.
 *
 * ПОЧЕМУ БОЛЬШЕ НЕ «СВЕСТИ К ПЕРВОМУ РАЗДЕЛУ». Прежде эта функция молча
 * возвращала первый раздел на любой неизвестный путь. Когда разделы
 * выдавались всем целиком, незнакомым был только опечатанный адрес. Теперь
 * незнакомым оказывается и адрес ЗАКРЫТОГО раздела — и молчаливый сброс
 * показал бы человеку, пришедшему по ссылке, чужой экран вместо причины
 * (техспека BL-2026-10-08-a315, §6 п. 2). Отсюда `denied` с названным
 * отказом и без правки адресной строки: подмена адреса скрыла бы, что он
 * пришёл не туда.
 *
 * ЧЕГО ФУНКЦИЯ ЗНАТЬ НЕ МОЖЕТ: существует ли раздел с таким путём вообще.
 * Пути невыданных разделов фронтенду не приходят — в этом и смысл выдачи.
 * Поэтому опечатка в адресе и закрытый раздел дают одно и то же решение
 * `denied`: различить их здесь нечем, а выдумывать разницу — значит завести
 * второй перечень разделов, от которого карточка и избавляется.
 *
 * ПОЧЕМУ КОРЕНЬ — ОСОБЫЙ СЛУЧАЙ. `/` — это и путь раздела «Сводка», и
 * просто вход в приложение. Пользователь, набравший адрес системы, по
 * ссылке на закрытый раздел не приходил, и встречать его отказом значило бы
 * показывать «недоступно» сразу после успешного входа. Поэтому корень без
 * выданной «Сводки» ведёт на первый выданный раздел — это `redirect`,
 * и адрес App.jsx заменяет на фактический, чтобы экран и адресная строка
 * не расходились.
 */
export function resolveSectionAccess(path, sections, canRender) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return { kind: "empty", section: null, path: null };
  }

  const granted = sections.find((s) => s.path === path);
  if (granted) {
    return {
      kind: canRender(granted.code) ? "section" : "unsupported",
      section: granted,
      path: granted.path,
    };
  }

  if (path === "/") {
    // Первый РИСУЕМЫЙ, а не просто первый: иначе вход приводил бы на
    // извещение «раздел не поддерживается» при наличии работающих разделов.
    const first = sections.find((s) => canRender(s.code)) || sections[0];
    return {
      kind: canRender(first.code) ? "redirect" : "unsupported",
      section: first,
      path: first.path,
    };
  }

  return { kind: "denied", section: null, path };
}

/**
 * Возвращает текущий путь и функции перехода.
 *
 * @returns {[string, function(string): void, function(string): void]}
 *          [путь, navigate, replace]
 *
 * Побочные эффекты: navigate добавляет запись в историю браузера
 * (window.history.pushState), replace подменяет текущую запись
 * (window.history.replaceState); подписка на popstate снимается при
 * размонтировании.
 */
export function useRoute() {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    // Кнопки «назад» и «вперёд» меняют адрес мимо React: без подписки на
    // popstate экран остался бы на прежнем разделе при изменившемся адресе.
    const onPop = () => setPath(currentPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next) => {
    // Повторный переход на тот же путь не пишем в историю: иначе «назад»
    // требовал бы столько нажатий, сколько раз ткнули в активный пункт меню.
    if (next === currentPath()) return;
    window.history.pushState(null, "", next);
    setPath(next);
  }, []);

  const replace = useCallback((next) => {
    // Замена записи, а не новая: так адрес правится ТОЛЬКО там, где экран
    // определился сам, а не по воле пользователя (корень роли без «Сводки»,
    // см. resolveSectionAccess). Запись в историю здесь была бы ловушкой:
    // «назад» вернул бы на корень, а корень снова перебросил бы сюда.
    if (next === currentPath()) return;
    window.history.replaceState(null, "", next);
    setPath(next);
  }, []);

  return [path, navigate, replace];
}
